import React, { useState, useEffect } from "react";

const MindReadingGame = () => {
  const [grid, setGrid] = useState([]);
  // 增加 'exploration' 模式
  const [gameMode, setGameMode] = useState("menu"); // menu, magician-mode, player-mode, exploration
  const [gameState, setGameState] = useState("setup");
  const [flippedPosition, setFlippedPosition] = useState(null);
  const [playerGuess, setPlayerGuess] = useState(null);
  const [player2Guess, setPlayer2Guess] = useState(null);
  const [score, setScore] = useState({
    player1: 0,
    player2: 0,
    magician: 0,
    audience: 0,
  });
  const [round, setRound] = useState(1);
  const [isFlipping, setIsFlipping] = useState(false);
  const [flipAnimation, setFlipAnimation] = useState(null);
  const [countdown, setCountdown] = useState(0);

  // 探究模式专有状态
  const [showBinary, setShowBinary] = useState(false); // 是否显示01编码

  // 播放提示音
  const playSound = () => {
    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
    oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.2);

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(
      0.01,
      audioContext.currentTime + 0.3
    );

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  };

  // 初始化网格，确保每行每列都是偶校验
  const initializeGrid = () => {
    const newGrid = Array(5)
      .fill()
      .map(() => Array(5).fill(false));

    // 随机填充前4行4列
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        newGrid[i][j] = Math.random() < 0.5;
      }
    }

    // 设置第5列，使每行为偶校验
    for (let i = 0; i < 4; i++) {
      const rowSum = newGrid[i]
        .slice(0, 4)
        .reduce((sum, val) => sum + (val ? 1 : 0), 0);
      newGrid[i][4] = rowSum % 2 === 1;
    }

    // 设置第5行，使每列为偶校验
    for (let j = 0; j < 5; j++) {
      const colSum = newGrid
        .slice(0, 4)
        .reduce((sum, row) => sum + (row[j] ? 1 : 0), 0);
      newGrid[4][j] = colSum % 2 === 1;
    }

    setGrid(newGrid);
    setFlippedPosition(null);
    setPlayerGuess(null);
    setPlayer2Guess(null);
    setIsFlipping(false);
    setFlipAnimation(null);
    setCountdown(0);
  };

  // 探究模式：点击硬币（直接翻转）
  const handleExplorationClick = (row, col) => {
    const newGrid = [...grid];
    newGrid[row] = [...newGrid[row]];
    newGrid[row][col] = !newGrid[row][col];
    setGrid(newGrid);
    // 在探究模式不记录 flippedPosition 用于判定输赢，纯粹为了观察
  };

  // 计算行校验值 (XOR sum)
  const getRowParity = (rowIndex) => {
    if (!grid[rowIndex]) return 0;
    const sum = grid[rowIndex].reduce((acc, val) => acc + (val ? 1 : 0), 0);
    return sum % 2;
  };

  // 计算列校验值 (XOR sum)
  const getColParity = (colIndex) => {
    if (grid.length === 0) return 0;
    let sum = 0;
    for (let i = 0; i < 5; i++) {
      sum += grid[i][colIndex] ? 1 : 0;
    }
    return sum % 2;
  };

  // 计算机翻转硬币（带动画）
  const computerFlipCoin = () => {
    setGameState("computer-flipping");
    setIsFlipping(true);

    const row = Math.floor(Math.random() * 5);
    const col = Math.floor(Math.random() * 5);

    setFlipAnimation({ row, col });

    setTimeout(() => {
      setGrid((prevGrid) =>
        prevGrid.map((r, i) =>
          r.map((c, j) => (i === row && j === col ? !c : c))
        )
      );
      setFlippedPosition({ row, col });
      setFlipAnimation(null);
      setIsFlipping(false);
      playSound();
      setGameState("magician-guessing");
    }, 2000);
  };

  // 玩家1翻转硬币（带动画）
  const player1FlipCoin = (row, col) => {
    if (gameState !== "ready") return;

    setGameState("player1-flipping");
    setIsFlipping(true);
    setFlipAnimation({ row, col });

    setTimeout(() => {
      setGrid((prevGrid) =>
        prevGrid.map((r, i) =>
          r.map((c, j) => (i === row && j === col ? !c : c))
        )
      );
      setFlippedPosition({ row, col });
      setFlipAnimation(null);
      setIsFlipping(false);
      setGameState("player2-guessing");
    }, 1500);
  };

  // 魔术师进行猜测
  const makeMagicianGuess = (row, col) => {
    if (gameState !== "magician-guessing") return;
    setPlayerGuess({ row, col });
    setGameState("round-complete");
  };

  // 玩家2进行猜测
  const makePlayer2Guess = (row, col) => {
    if (gameState !== "player2-guessing") return;
    setPlayer2Guess({ row, col });
    setGameState("round-complete");
  };

  // 开始新回合
  const nextRound = () => {
    // 更新分数逻辑 (保持原样)
    if (gameMode === "magician-mode") {
      if (
        playerGuess &&
        flippedPosition &&
        playerGuess.row === flippedPosition.row &&
        playerGuess.col === flippedPosition.col
      ) {
        setScore((prev) => ({ ...prev, magician: prev.magician + 1 }));
      } else {
        setScore((prev) => ({ ...prev, audience: prev.audience + 1 }));
      }
    } else if (gameMode === "player-mode") {
      if (
        player2Guess &&
        flippedPosition &&
        player2Guess.row === flippedPosition.row &&
        player2Guess.col === flippedPosition.col
      ) {
        setScore((prev) => ({ ...prev, player2: prev.player2 + 1 }));
      } else {
        setScore((prev) => ({ ...prev, player1: prev.player1 + 1 }));
      }
    }

    setRound((prev) => prev + 1);
    initializeGrid();

    if (gameMode === "magician-mode") {
      setGameState("countdown");
      setCountdown(5);
      const countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            computerFlipCoin();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else if (gameMode === "player-mode") {
      setGameState("ready");
    }
  };

  // 重置游戏
  const resetGame = () => {
    setGameMode("menu");
    setScore({ player1: 0, player2: 0, magician: 0, audience: 0 });
    setRound(1);
    setCountdown(0);
    setShowBinary(false);
    initializeGrid();
  };

  // 选择游戏模式
  const selectMode = (mode) => {
    setGameMode(mode);
    initializeGrid();

    if (mode === "magician-mode") {
      setGameState("countdown");
      setCountdown(5);
      const countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            computerFlipCoin();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else if (mode === "player-mode") {
      setGameState("ready");
    } else if (mode === "exploration") {
      setGameState("exploration");
      setShowBinary(true); // 探究模式默认开启二进制
    }
  };

  useEffect(() => {
    initializeGrid();
    setGameState("setup");
  }, []);

  return (
    <div className="max-w-4xl mx-auto p-6 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg min-h-screen">
      <h1 className="text-4xl font-bold text-center mb-6 text-indigo-800">
        🎩 神奇读心术表演 🎩
      </h1>

      {gameMode === "menu" && (
        <div className="text-center">
          <div className="bg-white rounded-lg p-8 shadow-lg mb-6">
            <h2 className="text-2xl font-semibold mb-6 text-gray-800">
              选择表演模式
            </h2>

            <div className="space-y-4">
              <button
                onClick={() => selectMode("magician-mode")}
                className="w-full bg-purple-600 text-white px-8 py-4 rounded-lg hover:bg-purple-700 transition-colors text-lg font-medium"
              >
                🎭 魔术师表演模式
                <div className="text-sm mt-1 opacity-90">
                  观众翻硬币，魔术师蒙眼猜测
                </div>
              </button>

              <button
                onClick={() => selectMode("player-mode")}
                className="w-full bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-colors text-lg font-medium"
              >
                👥 双人对战模式
                <div className="text-sm mt-1 opacity-90">
                  玩家1翻硬币，玩家2猜测
                </div>
              </button>

              {/* 新增探究模式按钮 */}
              <button
                onClick={() => selectMode("exploration")}
                className="w-full bg-green-600 text-white px-8 py-4 rounded-lg hover:bg-green-700 transition-colors text-lg font-medium border-2 border-green-500 shadow-sm"
              >
                🔬 原理探究模式
                <div className="text-sm mt-1 opacity-90">
                  转化为01编码，观察规律
                </div>
              </button>
            </div>

            <div className="mt-6 p-4 bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg">
              <p className="text-gray-700 font-medium">
                ✨ 见证不可思议的读心术奇迹 ✨
              </p>
            </div>
          </div>
        </div>
      )}

      {gameMode !== "menu" && (
        <>
          <div className="text-center mb-6">
            <div className="bg-white rounded-lg p-4 shadow-md">
              <div className="flex justify-between items-center mb-4">
                <div className="text-lg font-semibold">
                  {gameMode === "exploration"
                    ? "🔬 实验台"
                    : `第 ${round} 回合`}
                </div>

                {gameMode !== "exploration" && (
                  <div className="text-lg font-semibold">
                    {gameMode === "magician-mode"
                      ? `魔术师: ${score.magician} - ${score.audience} :观众`
                      : `玩家1: ${score.player1} - ${score.player2} :玩家2`}
                  </div>
                )}

                <div className="flex gap-2">
                  {/* 探究模式特有的控制按钮 */}
                  {gameMode === "exploration" && (
                    <button
                      onClick={() => setShowBinary(!showBinary)}
                      className={`px-4 py-2 rounded-lg transition-colors text-sm font-bold border ${
                        showBinary
                          ? "bg-indigo-100 text-indigo-700 border-indigo-300"
                          : "bg-yellow-100 text-yellow-700 border-yellow-300"
                      }`}
                    >
                      {showBinary ? "🔢 切换为硬币视图" : "01 切换为二进制视图"}
                    </button>
                  )}

                  <button
                    onClick={resetGame}
                    className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors text-sm"
                  >
                    返回菜单
                  </button>
                </div>
              </div>

              {/* 原有的模式提示代码 (省略部分未变动代码) */}
              {gameMode === "magician-mode" && gameState === "ready" && (
                <p className="text-gray-700">📋 初始布局已准备就绪</p>
              )}

              {/* 探究模式的提示 */}
              {gameMode === "exploration" && (
                <div className="text-left bg-green-50 p-3 rounded border border-green-100">
                  <p className="text-green-800 font-bold mb-1">🧪 实验指南：</p>
                  <ul className="text-sm text-green-700 list-disc list-inside space-y-1">
                    <li>
                      点击任意格子，观察右侧和底部的<strong>校验值</strong>
                      变化。
                    </li>
                    <li>
                      猜想这些<strong>校验值</strong>代表什么含义
                    </li>
                    <li>试着只翻转一枚硬币，猜想魔术师靠什么来定位的</li>
                  </ul>
                </div>
              )}

              {/* ... 其他原有提示保持不变 ... */}
            </div>
          </div>

          {/* 网格显示区域 - 针对探究模式做了布局调整 */}
          <div className="flex justify-center items-start">
            <div className="bg-white p-6 rounded-lg shadow-lg inline-block">
              {/* Grid Container: 探究模式下是 6x6 (5数据+1校验), 普通模式 5x5 */}
              <div
                className={`grid gap-3 transition-all duration-300`}
                style={{
                  gridTemplateColumns:
                    gameMode === "exploration"
                      ? "repeat(5, 1fr) auto"
                      : "repeat(5, 1fr)",
                }}
              >
                {grid.map((row, i) => (
                  <React.Fragment key={`row-${i}`}>
                    {/* 渲染每一行的硬币 */}
                    {row.map((coin, j) => (
                      <button
                        key={`${i}-${j}`}
                        onClick={() => {
                          if (gameMode === "exploration") {
                            handleExplorationClick(i, j);
                          } else if (
                            gameMode === "magician-mode" &&
                            gameState === "magician-guessing"
                          ) {
                            makeMagicianGuess(i, j);
                          } else if (
                            gameMode === "player-mode" &&
                            gameState === "ready"
                          ) {
                            player1FlipCoin(i, j);
                          } else if (
                            gameMode === "player-mode" &&
                            gameState === "player2-guessing"
                          ) {
                            makePlayer2Guess(i, j);
                          }
                        }}
                        disabled={
                          gameMode !== "exploration" &&
                          (gameState === "countdown" ||
                            gameState === "computer-flipping" ||
                            gameState === "player1-flipping" ||
                            gameState === "round-complete" ||
                            (gameMode === "magician-mode" &&
                              gameState === "ready"))
                        }
                        className={`
                          w-14 h-14 sm:w-16 sm:h-16 rounded-full border-3 transition-all duration-300 text-xl font-bold relative overflow-hidden flex items-center justify-center
                          ${
                            gameMode === "exploration" && showBinary
                              ? coin
                                ? "bg-gray-800 border-gray-600 text-white font-mono" // 1: 深色背景
                                : "bg-gray-100 border-gray-300 text-gray-400 font-mono" // 0: 浅色背景
                              : coin
                              ? "bg-yellow-400 border-yellow-600 text-yellow-900"
                              : "bg-gray-300 border-gray-500 text-gray-700"
                          }
                          ${
                            gameMode === "exploration" ||
                            (gameMode === "magician-mode" &&
                              gameState === "magician-guessing") ||
                            (gameMode === "player-mode" &&
                              (gameState === "ready" ||
                                gameState === "player2-guessing"))
                              ? "hover:scale-105 cursor-pointer hover:shadow-lg"
                              : "cursor-default"
                          }
                          /* 动画和高亮类名保持不变... */
                          ${
                            flipAnimation &&
                            flipAnimation.row === i &&
                            flipAnimation.col === j
                              ? "animate-spin scale-110 ring-4 ring-purple-400"
                              : ""
                          }
                          ${
                            flippedPosition &&
                            flippedPosition.row === i &&
                            flippedPosition.col === j &&
                            gameState === "round-complete"
                              ? "ring-4 ring-red-400"
                              : ""
                          }
                          ${
                            playerGuess &&
                            playerGuess.row === i &&
                            playerGuess.col === j &&
                            gameState === "round-complete"
                              ? "ring-4 ring-purple-400"
                              : ""
                          }
                        `}
                      >
                        {/* 内容显示控制 */}
                        {gameMode === "exploration" && showBinary
                          ? coin
                            ? "1"
                            : "0"
                          : coin
                          ? "正"
                          : "反"}
                      </button>
                    ))}

                    {/* 探究模式：每行右侧的校验位 */}
                    {gameMode === "exploration" && (
                      <div className="flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 border-l-2 border-dashed border-gray-300 pl-2">
                        <div
                          className={`
                            w-10 h-10 rounded flex items-center justify-center font-mono font-bold text-lg shadow-inner transition-colors duration-300
                            ${
                              getRowParity(i) === 0
                                ? "bg-green-100 text-green-600 border border-green-200" // 偶校验(正常)
                                : "bg-red-100 text-red-600 border border-red-200 scale-110 animate-pulse" // 奇校验(异常)
                            }
                          `}
                        >
                          {getRowParity(i)}
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))}

                {/* 探究模式：最底部的列校验位 */}
                {gameMode === "exploration" && (
                  <>
                    {/* 分隔线 */}
                    <div className="col-span-6 border-t-2 border-dashed border-gray-300 my-1"></div>

                    {/* 列校验位渲染 */}
                    {Array(5)
                      .fill()
                      .map((_, j) => (
                        <div
                          key={`col-parity-${j}`}
                          className="flex items-center justify-center"
                        >
                          <div
                            className={`
                            w-10 h-10 rounded flex items-center justify-center font-mono font-bold text-lg shadow-inner transition-colors duration-300
                            ${
                              getColParity(j) === 0
                                ? "bg-green-100 text-green-600 border border-green-200"
                                : "bg-red-100 text-red-600 border border-red-200 scale-110 animate-pulse"
                            }
                          `}
                          >
                            {getColParity(j)}
                          </div>
                        </div>
                      ))}

                    {/* 右下角的总校验位 (可选，这里放个图标或总状态) */}
                    <div className="flex items-center justify-center opacity-30 text-2xl">
                    ⊕
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default MindReadingGame;
